// src/modules/operaciones/inventarios/dashboard/PanelResumen.js
//
// Pestaña "Resumen" del dashboard de mermas:
//   - Fila de KPIs (venta, merma neta, pérdida bruta, sobrantes, merma conocida y desconocida)
//   - Gráfico de puente (waterfall): faltantes → merma conocida → sobrantes → merma neta
//   - Dona de participación de la venta por familia (Top 6 + Otros)
//
// USO
//   <PanelResumen analisis={d.analisis} />
//
// Espera el resultado de analizarDatos() en modo mermas: kpis, puente, familias.dona.
// Si no hay kpis (análisis vacío o informe genérico) muestra "No hay datos".
//
// NOTA TÉCNICA DEL PUENTE
// Cada barra va de `inicio` a `fin` usando una barra de RANGO (dataKey con [mín, máx]) en vez del
// truco de la barra invisible: así funciona igual cuando el acumulado cruza el cero. Por eso el
// tooltip oculta la serie "rango" y muestra el valor y el acumulado con `filasExtra`.

import React, { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertTriangle, HelpCircle, PackageMinus, PackagePlus, Scale, TrendingUp, BarChart3 } from "lucide-react";

import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import KpiCard from "./KpiCard";
import {
  COLORES_SEMANTICOS,
  COLOR_OTROS,
  PALETA_CATEGORIAS,
  fmtCompacto,
  fmtMoneda,
  fmtNumero,
  fmtPorcentaje,
  truncar,
} from "../utils/informesComun";

/* ========================================================================== */
/* Puente (waterfall)                                                         */
/* ========================================================================== */

const NOMBRE_CORTO = {
  faltantes: "Faltantes",
  conocida: "M. conocida",
  sobrantes: "Sobrantes",
  neto: "Merma neta",
};

function colorDePaso(p) {
  if (p.id === "faltantes") return COLORES_SEMANTICOS.perdida;
  if (p.id === "conocida") return COLORES_SEMANTICOS.conocida;
  if (p.id === "sobrantes") return COLORES_SEMANTICOS.sobrante;
  // Total: oscuro según el signo
  return p.valor < 0 ? COLORES_SEMANTICOS.perdidaOscuro : COLORES_SEMANTICOS.sobranteOscuro;
}

function datosPuente(puente) {
  return (puente || []).map((p) => ({
    ...p,
    nombre: NOMBRE_CORTO[p.id] || p.etiqueta,
    rango: [Math.min(p.inicio, p.fin), Math.max(p.inicio, p.fin)],
    color: colorDePaso(p),
  }));
}

function GraficoPuente({ datos }) {
  // Etiqueta de valor sobre cada barra
  const etiquetaBarra = (props) => {
    const { x, y, width, index } = props;
    const d = datos[index];
    if (!d || x === undefined) return null;
    const texto = `${d.tipo === "delta" && d.valor > 0 ? "+" : ""}${fmtCompacto(d.valor)}`;
    return (
      <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill="#374151">
        {texto}
      </text>
    );
  };

  return (
    <ComposedChart data={datos} margin={{ top: 24, right: 16, left: 8, bottom: 8 }}>
      <CartesianGrid vertical={false} stroke={COLORES_SEMANTICOS.grilla} />
      <XAxis dataKey="nombre" tick={{ fontSize: 11, fill: COLORES_SEMANTICOS.texto }} interval={0} tickLine={false} />
      <YAxis
        tickFormatter={(v) => fmtCompacto(v)}
        tick={{ fontSize: 11, fill: COLORES_SEMANTICOS.texto }}
        width={72}
        tickLine={false}
        axisLine={false}
      />
      <ReferenceLine y={0} stroke="#9ca3af" />
      <Tooltip
        cursor={{ fill: "rgba(0,0,0,0.04)" }}
        content={
          <ChartTooltip
            ocultar={["rango"]}
            colorearSigno
            titulo={(_, fila) => fila?.etiqueta}
            filasExtra={(f) => [
              {
                etiqueta: f.tipo === "total" ? "Merma neta" : "Variación",
                valor: f.valor,
                formato: "moneda",
                color: f.color,
              },
              ...(f.tipo === "delta" ? [{ etiqueta: "Acumulado", valor: f.fin, formato: "moneda" }] : []),
            ]}
          />
        }
      />
      <Bar dataKey="rango" maxBarSize={72} radius={[4, 4, 0, 0]} label={etiquetaBarra}>
        {datos.map((d) => (
          <Cell key={d.id} fill={d.color} />
        ))}
      </Bar>
    </ComposedChart>
  );
}

/* ========================================================================== */
/* Dona de familias                                                           */
/* ========================================================================== */

function GraficoDona({ datos, total }) {
  return (
    <PieChart>
      <Pie
        data={datos}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="44%"
        innerRadius="55%"
        outerRadius="80%"
        paddingAngle={2}
        stroke="#fff"
        strokeWidth={2}
      >
        {datos.map((d, i) => (
          <Cell key={d.name} fill={d.esOtros ? COLOR_OTROS : PALETA_CATEGORIAS[i % PALETA_CATEGORIAS.length]} />
        ))}
        <Label
          position="center"
          content={({ viewBox }) => {
            if (!viewBox || viewBox.cx === undefined) return null;
            return (
              <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                <tspan x={viewBox.cx} dy="-0.3em" fontSize={18} fontWeight={700} fill="#111827">
                  {fmtCompacto(total)}
                </tspan>
                <tspan x={viewBox.cx} dy="1.5em" fontSize={11} fill={COLORES_SEMANTICOS.texto}>
                  venta total
                </tspan>
              </text>
            );
          }}
        />
      </Pie>
      <Tooltip content={<ChartTooltip formato="moneda" etiquetas={{ value: "Venta" }} />} />
      <Legend
        verticalAlign="bottom"
        align="center"
        iconType="circle"
        iconSize={8}
        wrapperStyle={{ fontSize: 12 }}
        formatter={(valor, entry) => {
          const pct = entry?.payload?.pct;
          return `${truncar(valor, 18)}${typeof pct === "number" ? ` ${fmtPorcentaje(pct, 1)}` : ""}`;
        }}
      />
    </PieChart>
  );
}

/* ========================================================================== */
/* Panel                                                                      */
/* ========================================================================== */

function SinDatos() {
  return (
    <div className="flex flex-col items-center justify-center text-center bg-white border border-dashed border-gray-300 rounded-xl py-16 px-4">
      <BarChart3 className="w-10 h-10 text-gray-300 mb-3" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-700">No hay datos</p>
      <p className="text-xs text-gray-500 mt-1">Sube un informe de mermas o ajusta los filtros para ver el resumen.</p>
    </div>
  );
}

export function PanelResumen({ analisis }) {
  const k = analisis?.kpis;

  const puente = useMemo(() => datosPuente(analisis?.puente), [analisis?.puente]);
  const dona = analisis?.familias?.dona || [];
  const totalDona = useMemo(() => dona.reduce((s, f) => s + (Number(f.value) || 0), 0), [dona]);

  if (!analisis || !k) return <SinDatos />;

  // Cuánto de la pérdida bruta compensan los sobrantes
  const compensa = k.perdidaBruta < 0 && k.sobrantes > 0 ? (k.sobrantes / Math.abs(k.perdidaBruta)) * 100 : null;

  const notaPuente =
    k.perdidaBruta < 0 && k.sobrantes > 0
      ? `La merma neta (${fmtPorcentaje(k.mermaVentaPct)} de la venta) esconde una pérdida bruta de ` +
        `${fmtPorcentaje(k.perdidaBrutaPct)}: los sobrantes de la merma desconocida compensan parte de los faltantes.`
      : null;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard
          titulo="Venta total"
          valor={k.ventaTotal}
          formato="compacto"
          color="venta"
          icono={TrendingUp}
          subtitulo={`${fmtNumero(k.articulos.conVenta)} artículos con venta`}
        />
        <KpiCard
          titulo="Merma total neta"
          valor={k.mermaTotalNeta}
          formato="compacto"
          icono={Scale}
          subtitulo={`${fmtPorcentaje(k.mermaVentaPct)} de la venta`}
          ayuda="Merma desconocida neta + merma conocida"
        />
        <KpiCard
          titulo="Pérdida bruta"
          valor={k.perdidaBruta}
          formato="compacto"
          icono={AlertTriangle}
          subtitulo={`${fmtPorcentaje(k.perdidaBrutaPct)} de la venta`}
          ayuda="Faltantes de la merma desconocida + merma conocida (sin restar sobrantes)"
        />
        <KpiCard
          titulo="Sobrantes"
          valor={k.sobrantes}
          formato="compacto"
          color="sobrante"
          icono={PackagePlus}
          subtitulo={compensa !== null ? `Compensan ${fmtPorcentaje(compensa, 0)} de la pérdida bruta` : undefined}
          ayuda="Sobrantes de la merma desconocida"
        />
        <KpiCard
          titulo="Merma conocida"
          valor={k.mermaConocida}
          formato="compacto"
          color="conocida"
          icono={PackageMinus}
          subtitulo="Incluye entradas y salidas"
        />
        <KpiCard
          titulo="Merma desconocida"
          valor={k.mermaDesconocidaNeta}
          formato="compacto"
          color="desconocida"
          icono={HelpCircle}
          subtitulo={`Faltantes ${fmtCompacto(k.faltantesDesconocidos)} · Sobrantes ${fmtCompacto(k.sobrantesDesconocidos)}`}
          ayuda="Neto: faltantes + sobrantes"
        />
      </div>

      {/* Resumen de artículos */}
      <p className="text-xs text-gray-500 px-1">
        {fmtNumero(k.articulos.total)} artículos analizados · {fmtNumero(k.articulos.conMerma)} con merma ·{" "}
        {fmtNumero(k.articulos.sinMovimiento)} sin movimiento · merma neta negativa en{" "}
        {fmtNumero(k.articulos.mermaNegativa)}, positiva en {fmtNumero(k.articulos.mermaPositiva)}
      </p>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <ChartCard
          className="lg:col-span-3"
          titulo="Del faltante a la merma neta"
          subtitulo="Puente de mermas ($): cada barra suma o resta hasta llegar al neto"
          vacio={puente.length === 0}
          pie={notaPuente}
          height={340}
        >
          <GraficoPuente datos={puente} />
        </ChartCard>

        <ChartCard
          className="lg:col-span-2"
          titulo="Venta por familia"
          subtitulo={`Participación sobre ${fmtMoneda(k.ventaTotal)} (Top ${Math.max(dona.filter((d) => !d.esOtros).length, 1)} + Otros)`}
          vacio={dona.length === 0}
          mensajeVacio="No hay ventas para agrupar por familia."
          height={340}
        >
          <GraficoDona datos={dona} total={totalDona} />
        </ChartCard>
      </div>
    </div>
  );
}

export default PanelResumen;